import React, { useMemo } from 'react';
import { selfAssessmentLifecycleOf } from '../services/collaboration/collaborationShareKind';
import { PortalDataState } from '../components/PortalDataState';
import { PortalStatusPill } from '../components/PortalStatusPill';
import {
  inboundRelationshipStatusPresentation,
  receivedWorkStatusPresentation
} from '../services/collaboration/supplierCollaborationVocabulary';
import type { CollaborationEvidenceSummary } from './collaborationWorkQueueLogic';
import type {
  CollaborationInboxItem,
  CollaborationInstance
} from './collaborationControlPlaneShared';

interface CollaborationInboxCardProps {
  inbox: CollaborationInboxItem[];
  instanceById: Map<string, CollaborationInstance>;
  recipientThreadLoadingInboxItemId: string;
  newInboxItemIds: ReadonlySet<string>;
  evidenceByInboxItemId: ReadonlyMap<string, CollaborationEvidenceSummary>;
  relativeTime: (value: string) => string;
  parseJsonSafe: (value: string) => Record<string, unknown>;
  updatingRelationshipId: string;
  editingRelationshipId: string;
  relationshipDisplayNameDraft: string;
  savingRelationshipDisplayNameId: string;
  triagingInboxItemId: string;
  baselineActionInboxItemId: string;
  onUpdateRelationshipStatus: (relationshipId: string, status: 'accepted' | 'blocked') => void;
  onStartEditRelationshipDisplayName: (
    relationshipId: string,
    currentDisplayName: string,
    rowVersion: string
  ) => void;
  onChangeRelationshipDisplayNameDraft: (value: string) => void;
  onCancelEditRelationshipDisplayName: () => void;
  onSaveRelationshipDisplayName: (relationshipId: string) => void;
  onTriageInboxItem: (inboxItemId: string, status: 'accepted' | 'rejected' | 'archived') => void;
  onPullBaseline: (inboxItemId: string) => void;
  onAcknowledgeBaseline: (inboxItemId: string) => void;
  onOpenThread: (inboxItemId: string) => void;
  /**
   * Tracker #121: triage (accept, reject, archive) and customer-relationship
   * editing are for collaboration administrators; the server refuses anyone
   * else and the controls are hidden. Everyone sees and replies to the items
   * they are named on.
   */
  canManage?: boolean;
}

const EVIDENCE_MANIFEST_DISPLAY_NAME_CAP = 5;

const readEvidenceManifestNames = (payload: Record<string, unknown>): string[] =>
  Array.isArray(payload.evidenceManifest)
    ? (payload.evidenceManifest as unknown[])
        .map((entry) =>
          entry && typeof entry === 'object' && !Array.isArray(entry)
            ? String((entry as { name?: unknown }).name || '').trim()
            : ''
        )
        .filter(Boolean)
    : [];

const formatEvidenceManifestLine = (names: string[]): string => {
  const shown = names.slice(0, EVIDENCE_MANIFEST_DISPLAY_NAME_CAP).join(', ');
  const extra =
    names.length > EVIDENCE_MANIFEST_DISPLAY_NAME_CAP
      ? `, +${names.length - EVIDENCE_MANIFEST_DISPLAY_NAME_CAP} more`
      : '';
  return `Customer marked ${names.length} evidence item(s) for this package: ${shown}${extra}`;
};

const CollaborationInboxCard = ({
  inbox,
  instanceById,
  recipientThreadLoadingInboxItemId,
  newInboxItemIds,
  evidenceByInboxItemId,
  relativeTime,
  parseJsonSafe,
  updatingRelationshipId,
  editingRelationshipId,
  relationshipDisplayNameDraft,
  savingRelationshipDisplayNameId,
  triagingInboxItemId,
  baselineActionInboxItemId,
  onUpdateRelationshipStatus,
  onStartEditRelationshipDisplayName,
  onChangeRelationshipDisplayNameDraft,
  onCancelEditRelationshipDisplayName,
  onSaveRelationshipDisplayName,
  onTriageInboxItem,
  onPullBaseline,
  onAcknowledgeBaseline,
  onOpenThread,
  canManage = false
}: CollaborationInboxCardProps): JSX.Element => {
  const groupedInbox = useMemo(() => {
    const groups = new Map<string, CollaborationInboxItem[]>();
    inbox.forEach((item) => {
      const relationship = item.inboundCustomerRelationship;
      const key = relationship.inboundCustomerRelationshipId || relationship.sourceCustomerTenantId;
      groups.set(key, [...(groups.get(key) ?? []), item]);
    });
    return Array.from(groups.values()).sort((left, right) => {
      const leftUpdated = left[0]?.inboundCustomerRelationship.lastActivityAtUtc || left[0]?.updatedAt || '';
      const rightUpdated = right[0]?.inboundCustomerRelationship.lastActivityAtUtc || right[0]?.updatedAt || '';
      return rightUpdated.localeCompare(leftUpdated);
    });
  }, [inbox]);

  return (
    <section className="card collaboration-work-queue" aria-labelledby="received-work-heading">
      <div className="collaboration-section-heading">
        <div>
          <h2 id="received-work-heading" tabIndex={-1}>Received work</h2>
          <p className="muted">Work received from customers, grouped by the relationship it belongs to.</p>
        </div>
        <PortalStatusPill label={`${inbox.length} total`} />
      </div>
      {inbox.length === 0 ? (
        <PortalDataState
          compact
          kind="first-empty"
          heading="No received work yet"
          message="Work shared with this supplier organisation will appear here."
        />
      ) : (
      <div className="collaboration-work-list collaboration-work-list--grouped">
        {groupedInbox.map((items) => {
          const relationship = items[0].inboundCustomerRelationship;
          const relationshipId = relationship.inboundCustomerRelationshipId;
          const canActOnRelationship = canManage && Boolean(relationshipId) && relationship.status !== 'blocked';
          const relationshipIsBlocked = relationship.status === 'blocked';
          const displayName = relationship.sourceCustomerDisplayName || relationship.sourceCustomerTenantId;
          const isEditingDisplayName = editingRelationshipId === relationshipId;
          const isSavingDisplayName = savingRelationshipDisplayNameId === relationshipId;
          const relationshipStatus = inboundRelationshipStatusPresentation(relationship.status);
          return (
            <section
              key={relationshipId || relationship.sourceCustomerTenantId}
              style={{ display: 'grid', gap: '8px' }}
            >
              <div className="list-row" style={{ alignItems: 'center', cursor: 'default' }}>
                <div style={{ display: 'grid', gap: '4px' }}>
                  {isEditingDisplayName ? (
                    <label style={{ display: 'grid', gap: '4px', maxWidth: '360px' }}>
                      <span className="muted">Customer display name</span>
                      <input
                        className="input"
                        value={relationshipDisplayNameDraft}
                        maxLength={256}
                        onChange={(event) => onChangeRelationshipDisplayNameDraft(event.target.value)}
                        disabled={isSavingDisplayName}
                      />
                    </label>
                  ) : (
                    <strong>{displayName}</strong>
                  )}
                  <div className="muted">
                    {items.length} inbound item{items.length === 1 ? '' : 's'} | last activity{' '}
                    {relativeTime(relationship.lastActivityAtUtc)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <PortalStatusPill label={relationshipStatus.label} tone={relationshipStatus.tone} />
                  {canManage && relationshipId && !isEditingDisplayName && (
                    <button
                      className="btn secondary"
                      type="button"
                      onClick={() => onStartEditRelationshipDisplayName(relationshipId, displayName, relationship.rowVersion)}
                    >
                      Rename
                    </button>
                  )}
                  {isEditingDisplayName && (
                    <>
                      <button
                        className="btn secondary"
                        type="button"
                        onClick={() => onSaveRelationshipDisplayName(relationshipId)}
                        disabled={isSavingDisplayName || !relationshipDisplayNameDraft.trim()}
                      >
                        {isSavingDisplayName ? 'Saving...' : 'Save name'}
                      </button>
                      <button
                        className="btn secondary"
                        type="button"
                        onClick={onCancelEditRelationshipDisplayName}
                        disabled={isSavingDisplayName}
                      >
                        Cancel
                      </button>
                    </>
                  )}
                  {canActOnRelationship && relationship.status !== 'accepted' && (
                    <button
                      className="btn secondary"
                      type="button"
                      onClick={() => onUpdateRelationshipStatus(relationshipId, 'accepted')}
                      disabled={updatingRelationshipId === relationshipId}
                    >
                      {updatingRelationshipId === relationshipId ? 'Updating...' : 'Accept customer'}
                    </button>
                  )}
                  {canActOnRelationship && (
                    <button
                      className="btn secondary"
                      type="button"
                      onClick={() => onUpdateRelationshipStatus(relationshipId, 'blocked')}
                      disabled={updatingRelationshipId === relationshipId}
                    >
                      {updatingRelationshipId === relationshipId ? 'Updating...' : 'Block'}
                    </button>
                  )}
                </div>
              </div>
              <details className="collaboration-work-row__details">
                <summary>Customer relationship details</summary>
                <div className="muted">Source tenant {relationship.sourceCustomerTenantId}</div>
              </details>
              {relationshipIsBlocked && (
                <div className="alert subtle">
                  This inbound customer relationship is read-only. Existing records remain visible, but new source-visible
                  triage, baseline pulls, and supplier responses are blocked.
                </div>
              )}

              {items.map((item) => {
                const payload = parseJsonSafe(item.invitation.payloadJson);
                const evidenceManifestNames = readEvidenceManifestNames(payload);
                const evidence = evidenceByInboxItemId.get(item.inboxItemId);
                const itemInstance = item.instanceId ? instanceById.get(item.instanceId) : undefined;
                const isLoading = recipientThreadLoadingInboxItemId === item.inboxItemId;
                const isTriaging = triagingInboxItemId === item.inboxItemId;
                const isBaselineAction = baselineActionInboxItemId === item.inboxItemId;
                const canAcceptItem =
                  canManage && !relationshipIsBlocked && item.status === 'pending' && item.invitation.status === 'pending';
                const canRejectItem =
                  canManage && !relationshipIsBlocked && item.status === 'pending' && item.invitation.status === 'pending';
                const canArchiveItem = canManage && item.status !== 'archived' && item.status !== 'imported';
                const canPullBaseline = !relationshipIsBlocked && item.updateAvailable;
                const status = receivedWorkStatusPresentation(item.status);
                const isNew = newInboxItemIds.has(item.inboxItemId);
                return (
                  <article className="collaboration-work-row" key={item.inboxItemId}>
                    <button
                      className="collaboration-work-row__main"
                      type="button"
                      onClick={() => onOpenThread(item.inboxItemId)}
                      disabled={isLoading || !item.runtimeAccess.canOpenInstallationThread}
                    >
                      <span className="collaboration-work-row__title-line">
                        <strong>{String(payload.title || (item.invitation.kind === 'self_assessment' ? 'Supplier self-assessment' : 'Shared finding'))}</strong>
                        {isNew && <PortalStatusPill label="New" tone="warning" />}
                      </span>
                      {item.invitation.kind === 'self_assessment' ? (
                        <span className="muted">Self-assessment questionnaire · {String(payload.templateKey || payload.auditTemplateKey || 'template unavailable')}</span>
                      ) : (
                        <span className="muted">Finding {String(payload.findingId || 'reference unavailable')}</span>
                      )}
                      <span className="muted">Updated {relativeTime(item.updatedAt)} · invitation expires {new Date(item.invitation.expiresAt).toLocaleString()}</span>
                      <span className="collaboration-work-row__status-line">
                        <PortalStatusPill label={status.label} tone={status.tone} />
                      {item.invitation.kind === 'self_assessment' && (
                          <PortalStatusPill label={`Questionnaire ${selfAssessmentLifecycleOf(item.invitation)}`} tone="neutral" />
                      )}
                      {item.updateAvailable && <PortalStatusPill label="Update available" tone="warning" />}
                      {item.pendingBaselineAcknowledgement && <PortalStatusPill label="Acknowledgement required" tone="warning" />}
                      </span>
                      <span className="muted">
                        {evidence?.label || (evidenceManifestNames.length > 0
                          ? formatEvidenceManifestLine(evidenceManifestNames)
                          : 'No evidence declared in the original package')}
                      </span>
                      <span className="collaboration-work-row__open">
                        {item.runtimeAccess.canOpenInstallationThread
                          ? isLoading ? 'Opening…' : 'Open thread →'
                          : item.runtimeAccess.message}
                      </span>
                    </button>
                    <div className="collaboration-work-row__actions">
                      {canPullBaseline && (
                        <button
                          className="btn secondary"
                          type="button"
                          onClick={() => onPullBaseline(item.inboxItemId)}
                          disabled={isBaselineAction}
                        >
                          {isBaselineAction ? 'Updating...' : 'Pull update'}
                        </button>
                      )}
                      {item.pendingBaselineAcknowledgement && (
                        <button
                          className="btn secondary"
                          type="button"
                          onClick={() => onAcknowledgeBaseline(item.inboxItemId)}
                          disabled={isBaselineAction}
                        >
                          {isBaselineAction ? 'Updating...' : 'Acknowledge baseline'}
                        </button>
                      )}
                      {canAcceptItem && (
                        <button
                          className="btn secondary"
                          type="button"
                          onClick={() => onTriageInboxItem(item.inboxItemId, 'accepted')}
                          disabled={isTriaging}
                        >
                          {isTriaging ? 'Updating...' : 'Accept item'}
                        </button>
                      )}
                      {canRejectItem && (
                        <button
                          className="btn secondary"
                          type="button"
                          onClick={() => onTriageInboxItem(item.inboxItemId, 'rejected')}
                          disabled={isTriaging}
                        >
                          {isTriaging ? 'Updating...' : 'Reject item'}
                        </button>
                      )}
                      {canArchiveItem && (
                        <button
                          className="btn secondary"
                          type="button"
                          onClick={() => onTriageInboxItem(item.inboxItemId, 'archived')}
                          disabled={isTriaging}
                        >
                          {isTriaging ? 'Updating...' : 'Archive'}
                        </button>
                      )}
                    </div>
                    <details className="collaboration-work-row__details">
                      <summary>Connection details</summary>
                      {(item.latestSourceBaselineVersion || item.localBaselineVersion) && (
                        <div className="muted">
                          Source baseline {item.latestSourceBaselineVersion || 'unavailable'} · local {item.localBaselineVersion || 'not pulled'}
                        </div>
                      )}
                      {itemInstance && (
                        <div className="muted">
                          Installation {itemInstance.displayName || itemInstance.siteUrl} · outbound {itemInstance.collaborationOutbound ? 'on' : 'off'} · inbound {itemInstance.collaborationInbound ? 'on' : 'off'}
                        </div>
                      )}
                      <div className="muted">{item.runtimeAccess.message}</div>
                    </details>
                  </article>
                );
              })}
            </section>
          );
        })}
      </div>
      )}
    </section>
  );
};

export default CollaborationInboxCard;
